import {
  Directory,
  DirectoryBrowser,
  File,
  MediaLibrary,
  MediaLibrarySearchOption,
  Panel,
  PanelGroup,
  PanelResizeHandle,
  isFile,
} from '@southfields-digital/mxmz-react-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

import { MEDIA_LIBRARY_FILES_PAGE_SIZE } from 'src/config/mediaLibrary';
import { InjectedBaseLayoutContextProps, injectBaseLayoutContext } from 'src/layouts/BaseLayout';
import { WebsocketBuddy } from 'src/services/websockets';
import { CopyFilesParams } from 'src/types/mediaLibrary';

import styles from './MediaLibrary.module.scss';
import type { MediaLibraryPageProps } from './MediaLibrary.types';
import { getRawPath } from './MediaLibrary.utils';

const workspaceDirectory: Directory = { id: 'root', name: 'Workspace', preventDeletion: true };

const MediaLibraryPage = ({
  baseLayoutContext: { setFullWidth },
  copyDialogDirectories,
  copyFiles,
  count,
  createFolderAtPath,
  deleteDirectory,
  deleteFile,
  directories,
  fetchCopyDialogDirectories,
  files,
  getDataByDirectory,
  getFileById,
  isCopying,
  isLoading,
  isLocked,
  renameFile,
  searchCount,
  searchDataByDirectory,
  searchResults,
  updateDirectoryName,
  uploadFiles,
  workspaceId,
}: MediaLibraryPageProps & InjectedBaseLayoutContextProps) => {
  const pageSize = MEDIA_LIBRARY_FILES_PAGE_SIZE;

  // State
  const [activeIds, setActiveIds] = useState(['root']);
  const [currentDirectory, setCurrentDirectory] = useState(workspaceDirectory);
  const [currentPage, setCurrentPage] = useState(1);
  const [fontsDirectoryId, setFontsDirectoryId] = useState<string | undefined>(undefined);
  const [directoryName, setDirectoryName] = useState(currentDirectory?.name);
  const [hasMore, setHasMore] = useState(false);
  const [searchOption, setSearchOption] = useState<MediaLibrarySearchOption>({
    id: currentDirectory.id,
    label: currentDirectory.name,
  });
  const [currentDirectoryLocked, setCurrentDirectoryLocked] = useState(isLocked);

  const [searchTerm, setSearchTerm] = useState('');

  // Current directory is volatile and likely to change during API requests, so define a ref for use in callbacks
  const guaranteedCurrentDirectory = useRef<Directory>(currentDirectory);
  // Current page is volatile and likely to change during API requests, so define a ref for use in callbacks
  const guaranteedCurrentPage = useRef<number>(currentPage);
  // Files are volatile and likely to change during API requests, so define a ref for use in callbacks
  const guaranteedFiles = useRef<File[]>(files);
  const previousSearchTerm = useRef('');
  const socketInstance = useRef<Socket | null>(null);
  const switchingDirectories = useRef(false);

  // Memoized data
  const loadingNextDirectory = useMemo(
    () => isLoading && switchingDirectories.current,
    [isLoading]
  );
  const data = useMemo(
    () => (loadingNextDirectory ? [] : searchTerm ? searchResults : files),
    [files, loadingNextDirectory, searchResults, searchTerm]
  );
  const searchOptions = useMemo(
    () =>
      currentDirectory.id !== 'root'
        ? [workspaceDirectory, { id: currentDirectory.id, name: 'Current folder' }].map(
            ({ id, name }) => ({ id, label: name })
          )
        : [],
    [currentDirectory.id]
  );

  // Handlers
  const deleteDirectoryHandler = useCallback(
    (directoryId: string) => {
      deleteDirectory({
        id: directoryId,
        callback: () => {
          setCurrentDirectory(workspaceDirectory);
          setActiveIds(['root']);
          getDataByDirectory({ id: 'root' });
        },
      });
    },
    [setCurrentDirectory, setActiveIds, getDataByDirectory]
  );

  const getDataHandler = useCallback(
    (directory: Directory) => {
      switchingDirectories.current = true;

      // Update state
      setSearchTerm('');
      setCurrentPage(1);
      setCurrentDirectory(directory || workspaceDirectory);
      setCurrentDirectoryLocked(false);

      // Fetch data
      getDataByDirectory({
        id: directory.id || workspaceDirectory.id,
        pagination: { offset: 0, pageSize },
      });
    },
    [setSearchTerm, setCurrentPage, setCurrentDirectory, setCurrentDirectoryLocked]
  );

  const submitFilesHandler = useCallback(
    (fileList: FileList | null) => {
      const directoryId = currentDirectory.id;

      if (fileList && fileList?.length > 0) {
        uploadFiles({
          directoryId,
          fileList,
          callback: (uploadedFileCount: number) => {
            // Refresh data if current directory is unchanged at the callback
            if (guaranteedCurrentDirectory.current.id === directoryId) {
              // Correct for fixed page size and variable uploaded file count
              const currentFileLength =
                uploadedFileCount + guaranteedCurrentPage.current * pageSize;
              const correctedPageSize = currentFileLength + (currentFileLength % pageSize);
              const currentPageDiff = correctedPageSize / pageSize - guaranteedCurrentPage.current;

              // Correct the current page size to keep infinite scroll working
              if (currentPageDiff > 0) {
                setCurrentPage(guaranteedCurrentPage.current + currentPageDiff);
              }

              getDataByDirectory({
                id: directoryId,
                pagination: { offset: 0, pageSize: correctedPageSize },
                hideLoader: true,
              });
            }
          },
        });
      }
    },
    [currentDirectory]
  );

  const onDirectoryExpandHandler = (directoryId: string) => {
    // Lazy load directory children when expanded in the copy dialog tree
    fetchCopyDialogDirectories(directoryId);
  };

  const copyFilesHandler = useCallback(
    ({ files, destinationDirectoryId, callback }: CopyFilesParams) => {
      copyFiles(files, destinationDirectoryId, (copiedFileCount: number) => {
        // Refresh data if current directory is the same as the destination directory
        if (guaranteedCurrentDirectory.current.id === destinationDirectoryId) {
          // Correct for fixed page size and variable copied file count
          const currentFileLength = copiedFileCount + guaranteedCurrentPage.current * pageSize;
          const correctedPageSize = currentFileLength + (currentFileLength % pageSize);
          const currentPageDiff = correctedPageSize / pageSize - guaranteedCurrentPage.current;

          // Correct the current page size to keep infinite scroll working
          if (currentPageDiff > 0) {
            setCurrentPage(guaranteedCurrentPage.current + currentPageDiff);
          }

          getDataByDirectory({
            id: destinationDirectoryId,
            pagination: { offset: 0, pageSize: correctedPageSize },
            hideLoader: true,
          });
        }

        // Call the optional callback from the component library
        if (callback) {
          callback();
        }
      });
    },
    [currentDirectory]
  );

  const refreshCurrentDirectoryOnReceiveWebsocketMessage = useCallback(
    ({ directoryId }: { directoryId: string }) => {
      // Get the latest data when a directory is updated
      if (directoryId === guaranteedCurrentDirectory.current.id) {
        setTimeout(() => {
          getDataByDirectory({
            id: guaranteedCurrentDirectory.current.id,
            pagination: { offset: 0, pageSize: currentPage * pageSize },
            hideLoader: true,
          });
        }, 10000); // Delay to allow Lamba's to process thumbs / previews
      }
    },
    [currentDirectory, currentPage]
  );

  // useEffect hooks
  useEffect(() => {
    getDataByDirectory({ id: 'root' });
    fetchCopyDialogDirectories('root');
    setFullWidth(true);

    if (workspaceId) {
      const { socket } = WebsocketBuddy({
        channel: btoa(`${workspaceId}:::mediaLibrary`),
        driver: 'SocketIo',
        listeners: {
          directoryLocked: ({ directoryId }) => {
            if (directoryId === guaranteedCurrentDirectory.current.id) {
              setCurrentDirectoryLocked(true);
            }
          },
          directoryLockReleased: ({ directoryId }) => {
            if (directoryId === guaranteedCurrentDirectory.current.id) {
              setCurrentDirectoryLocked(false);
            }
          },
          directoryUpdated: refreshCurrentDirectoryOnReceiveWebsocketMessage,
          fileUpdated: ({ fileId }: { fileId: string }) => {
            // Only refresh the file if it exists in the current state
            const fileExists = guaranteedFiles.current.some((file) => file.id === fileId);
            if (fileExists) {
              getFileById(fileId);
            }
          },
        },
      });

      socketInstance.current = socket;
    }

    return function unmount() {
      setFullWidth(false);

      if (socketInstance.current) {
        socketInstance?.current.disconnect();
        socketInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setCurrentDirectoryLocked(isLocked);
  }, [isLocked]);

  useEffect(() => {
    guaranteedCurrentDirectory.current = currentDirectory;
    setDirectoryName(currentDirectory.name);
    setSearchOption({ id: currentDirectory.id, label: currentDirectory.name });
  }, [currentDirectory]);

  useEffect(() => {
    guaranteedFiles.current = files;
  }, [files]);

  useEffect(() => {
    guaranteedCurrentPage.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    const searchCleared = !searchTerm && previousSearchTerm.current.length > searchTerm.length;

    // Update state
    setCurrentPage(1);

    // Search if search term is not empty
    if (searchTerm) {
      // Set directory name
      setDirectoryName(searchOption.label);

      // Hide loader during API request
      switchingDirectories.current = false;

      searchDataByDirectory({
        id: searchOption.id,
        pagination: { offset: 0, pageSize },
        searchTerm,
      });
    }

    // Get data normally if search term is empty
    if (searchCleared) {
      // Set directory name
      setDirectoryName(currentDirectory.name);

      // Show loader during API request
      switchingDirectories.current = true;

      getDataByDirectory({
        id: currentDirectory.id,
        pagination: { offset: 0, pageSize },
      });
    }

    previousSearchTerm.current = searchTerm;
  }, [currentDirectory, searchTerm, searchOption]);

  useEffect(() => {
    if (searchTerm) {
      setHasMore(searchCount > files.length && searchCount > pageSize * currentPage);
    } else {
      setHasMore(count > files.length && count > pageSize * currentPage);
    }
  }, [count, files, currentPage, searchCount, searchTerm]);

  // Find and set fonts directory ID from root directories
  useEffect(() => {
    if (directories && directories.length > 0) {
      const rootDirectory = directories.find((dir) => dir.id === 'root');
      if (rootDirectory?.directories) {
        const fontsDir = rootDirectory.directories.find(
          (dir) => dir.name.toLowerCase() === 'fonts'
        );
        if (fontsDir) {
          setFontsDirectoryId(fontsDir.id);
        }
      }
    }
  }, [directories]);

  return (
    <div className={styles.Library}>
      <PanelGroup direction="vertical">
        <Panel
          defaultSize={25}
          minSize={15}
          // Hide the directory browser panel when there is a search term
          maxSize={searchTerm ? 0 : undefined}
        >
          <DirectoryBrowser
            activeIds={activeIds}
            createDirectory={(path) =>
              new Promise((resolve) => {
                createFolderAtPath({
                  path: getRawPath(path),
                  insertIntoDirectoryId: activeIds.slice(-1)[0],
                  callback: resolve,
                });
              })
            }
            data={(directories || []) as Directory[]}
            getData={getDataHandler}
            setActiveIds={setActiveIds}
            onDeleteConfirm={deleteDirectoryHandler}
          />
        </Panel>

        {!searchTerm && <PanelResizeHandle />}

        <Panel defaultSize={75} minSize={15}>
          <MediaLibrary
            key={`${currentDirectory.id}_${directoryName}`}
            deleteFile={({ id }) => deleteFile(id)}
            renameFile={(file, newName) => renameFile(file.id, newName)}
            directoryName={directoryName}
            hasMore={hasMore}
            itemsLength={pageSize * currentPage}
            isLoading={isLoading}
            isLocked={currentDirectoryLocked}
            isRoot={currentDirectory.id === 'root'}
            libraryData={data}
            folderContext={{
              currentDirectory: {
                name: currentDirectory.name,
                id: currentDirectory.id,
              },
              activeIds,
              fontsDirectoryId,
            }}
            loadMore={() => {
              setCurrentPage((prevPage) => {
                switchingDirectories.current = false;

                const nextPage = prevPage + 1;
                const nextPageZeroIndexed = nextPage - 1; // For clarity

                if (searchTerm) {
                  searchDataByDirectory({
                    id: searchOption.id,
                    pagination: {
                      offset: nextPageZeroIndexed * pageSize,
                      pageSize,
                    },
                    searchTerm,
                  });
                } else {
                  getDataByDirectory({
                    id: currentDirectory.id,
                    pagination: {
                      offset: nextPageZeroIndexed * pageSize,
                      pageSize,
                    },
                  });
                }

                return nextPage;
              });
            }}
            onItemClick={(item) => {
              if (!isFile(item)) {
                getDataHandler(item as Directory);
              }
            }}
            searchOption={searchOption}
            searchOptions={searchTerm ? searchOptions : []}
            searchQuery={searchTerm}
            setSearchOption={(searchOption: MediaLibrarySearchOption) => {
              setSearchOption(searchOption);
              setDirectoryName(searchOption?.label);
            }}
            setSearchQuery={setSearchTerm}
            supportedMimeTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/webp']}
            updateDirectoryName={({ updatedDirectoryName, onSuccess, onFailure }) => {
              setCurrentDirectoryLocked(true);
              updateDirectoryName({
                id: currentDirectory.id,
                name: updatedDirectoryName,
                onSuccess,
                onFailure,
              });
            }}
            uploadFiles={submitFilesHandler}
            directories={copyDialogDirectories.map((directory) => ({
              ...directory,
              defaultExpanded: directory.id === 'root',
            }))}
            copyFiles={copyFilesHandler}
            isCopyLoading={isCopying}
            onDirectoryExpand={onDirectoryExpandHandler}
            variant="full"
          />
        </Panel>
      </PanelGroup>
    </div>
  );
};

export default injectBaseLayoutContext(MediaLibraryPage);
